import React, { useState, useMemo, useEffect } from 'react';
import { Info } from 'lucide-react';
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, deleteDoc } from 'firebase/firestore';
import { app } from './firebase';

// Import Components
import Header from './components/Header';
import Navigation from './components/Navigation';
import ResetModal from './components/ResetModal';
import RemovePlayerModal from './components/RemovePlayerModal';
import RenamePlayerModal from './components/RenamePlayerModal';
import ConfirmUpdateModal from './components/ConfirmUpdateModal';
import Setup from './components/Setup';
import Matches from './components/Matches';
import Ranking from './components/Ranking';
import MatchDetail from './components/MatchDetail';
import FloatingSummary from './components/FloatingSummary';
import PreviousTournaments from './components/PreviousTournaments';
import SaveStatus from './components/SaveStatus';
import LandingPage from './components/LandingPage';

const auth = getAuth(app);
const db = getFirestore(app);

const App = () => {
  const [user, setUser] = useState(null);
  const [players, setPlayers] = useState([]);
  const [playerName, setPlayerName] = useState('');
  const [matchesPerPlayer, setMatchesPerPlayer] = useState(0);
  const [matches, setMatches] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [view, setView] = useState('setup'); // 'setup', 'matches', 'ranking', 'match_detail'
  const [activeMatchId, setActiveMatchId] = useState(null);
  const [error, setError] = useState('');
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [playerToRemove, setPlayerToRemove] = useState(null);
  const [playerToRename, setPlayerToRename] = useState(null);
  const [showConfirmUpdate, setShowConfirmUpdate] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [showPrevious, setShowPrevious] = useState(false);
  const [tournamentName, setTournamentName] = useState(`Torneio ${new Date().toLocaleString()}`);
  const [activeTournamentId, setActiveTournamentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const saveState = async (state) => {
    if (!user) return;
    setIsSaving(true);
    const docRef = doc(db, 'users', user.uid, 'tournaments', 'current');
    await setDoc(docRef, state);
    setIsSaving(false);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        const docRef = doc(db, 'users', user.uid, 'tournaments', 'current');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setPlayers(data.players || []);
          setMatches(data.matches || []);
          setMatchesPerPlayer(data.matchesPerPlayer || 0);
          setTournamentName(data.tournamentName || `Torneio ${new Date().toLocaleString()}`);
          setActiveTournamentId(data.activeTournamentId || null);
          setView(data.view || 'setup');
        } else {
          // No current tournament, reset to default state
          setPlayers([]);
          setMatches([]);
          setMatchesPerPlayer(0);
          setTournamentName(`Torneio ${new Date().toLocaleString()}`);
          setActiveTournamentId(null);
          setView('setup');
        }
      } else {
        setUser(null);
        // Reset state when user logs out
        setPlayers([]);
        setMatches([]);
        setMatchesPerPlayer(0);
        setTournamentName(`Torneio ${new Date().toLocaleString()}`);
        setActiveTournamentId(null);
        setView('setup');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Timer loop for real-time updates
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const login = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Error during sign-in:", error);
      setError("Failed to sign in.");
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error during sign-out:", error);
      setError("Failed to sign out.");
    }
  };


  // Calculate valid matches per player options
  const validOptions = useMemo(() => {
    const options = [];
    const n = players.length;
    if (n < 4) return [];
    
    for (let m = 1; m <= 10; m++) {
      if ((n * m) % 4 === 0) {
        options.push(m);
      }
    }
    return options;
  }, [players.length]);

  useEffect(() => {
    if (validOptions.length === 0 && players.length > 0) {
      setMatchesPerPlayer(0);
    }
  }, [validOptions, players.length]);

  const hasCompletedMatches = useMemo(() => {
    return matches.some(m => m.completed);
  }, [matches]);

  const filteredMatches = useMemo(() => {
    if (selectedFilters.length === 0) return matches;
    return matches.filter(m => {
      const allPlayersInMatch = [...m.pair1, ...m.pair2];
      return selectedFilters.every(filterPlayer => allPlayersInMatch.includes(filterPlayer));
    });
  }, [matches, selectedFilters]);
    
  const partnerMapping = useMemo(() => {
    if (selectedFilters.length === 0) return null;
    const mapping = {};
    selectedFilters.forEach(filteredPlayer => {
      const partners = new Set();
      matches.forEach(m => {
        if (m.pair1.includes(filteredPlayer)) {
          m.pair1.forEach(p => { if (p !== filteredPlayer) partners.add(p); });
        } else if (m.pair2.includes(filteredPlayer)) {
          m.pair2.forEach(p => { if (p !== filteredPlayer) partners.add(p); });
        }
      });
      mapping[filteredPlayer] = Array.from(partners).sort();
    });
    return mapping;
  }, [matches, selectedFilters]);

  const generateMatches = () => {
    const persistedMatches = matches.filter(m => m.completed);
    
    if (players.length < 4) {
      setError('São necessários pelo menos 4 jogadores.');
      return;
    }
    
    const totalSlots = players.length * matchesPerPlayer;
    if (totalSlots % 4 !== 0) {
        setError('A combinação de jogadores e partidas por jogador não é possível. O total de vagas de jogadores (jogadores x partidas) deve ser divisível por 4.');
        return;
    }

    let success = false;
    let attempts = 0;
    let generatedMatches = [];
    
    const playerMatchCounts = {};
    players.forEach(p => playerMatchCounts[p] = 0);
    persistedMatches.forEach(m => {
        [...m.pair1, ...m.pair2].forEach(p => {
            if (playerMatchCounts.hasOwnProperty(p)) {
                playerMatchCounts[p]++;
            }
        });
    });

    while (!success && attempts < 500) {
      attempts++;
      const pool = [];
      
      players.forEach(p => {
        const matchesNeeded = matchesPerPlayer - (playerMatchCounts[p] || 0);
        for (let i = 0; i < matchesNeeded; i++) {
            pool.push(p);
        }
      });
      
      if (pool.length === 0) {
        generatedMatches = [];
        success = true;
        break;
      }

      for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pool[i], pool[j]] = [pool[j], pool[i]];
      }

      const tempMatches = [];
      const usedPairs = new Set();
      persistedMatches.forEach(m => {
          usedPairs.add([...m.pair1].sort().join('∞'));
          usedPairs.add([...m.pair2].sort().join('∞'));
      });

      let possible = true;
      for (let i = 0; i < pool.length; i += 4) {
          if (i + 4 > pool.length) { 
              possible = false;
              break;
          }

        const gamePlayers = pool.slice(i, i + 4);
        if (new Set(gamePlayers).size !== 4) {
          possible = false;
          break;
        }

        const p1 = [gamePlayers[0], gamePlayers[1]];
        const p2 = [gamePlayers[2], gamePlayers[3]];
        const key1 = [...p1].sort().join('∞');
        const key2 = [...p2].sort().join('∞');

        if (usedPairs.has(key1) || usedPairs.has(key2)) {
          possible = false;
          break;
        }

        usedPairs.add(key1);
        usedPairs.add(key2);

        tempMatches.push({
          pair1: p1,
          pair2: p2,
          score1: 0,
          score2: 0,
          winner: null,
          completed: false,
          isRunning: false,
          firstStartTime: null,
          lastStartTime: null,
          endTime: null,
          accumulatedTime: 0
        });
      }

      if (possible) {
        generatedMatches = tempMatches;
        success = true;
      }
    }

    if (success) {
      const finalMatches = [...persistedMatches, ...generatedMatches].map((m, index) => ({
          ...m,
          id: index + 1
      }));
      setMatches(finalMatches);
      setSelectedFilters([]);
      setView('matches');
      setError('');
      saveState({ players, matches: finalMatches, view: 'matches', matchesPerPlayer, tournamentName, activeTournamentId });
    } else {
      setError('Configuração difícil. Não foi possível gerar novas partidas. Tente um número diferente de partidas ou reinicie o torneio.');
    }
  };

  const handleGenerateOrUpdate = () => {
    const uncompletedCount = matches.filter(m => !m.completed).length;
    if (uncompletedCount > 0) {
        setShowConfirmUpdate(true);
    } else {
        generateMatches();
    }
  };

  const confirmUpdateAndProceed = () => {
    setShowConfirmUpdate(false);
    generateMatches();
  };

  const addPlayer = () => {
    const trimmed = playerName.trim();
    if (!trimmed) return;
    if (players.includes(trimmed)) {
      setError('Este jogador já foi adicionado.');
      return;
    }
    const newPlayers = [...players, trimmed];
    setPlayers(newPlayers);
    setPlayerName('');
    setError('');
    saveState({ players: newPlayers, matches, view, matchesPerPlayer, tournamentName, activeTournamentId });
  };

  const confirmRemovePlayer = () => {
    if (!playerToRemove) return;

    const updatedPlayers = players.filter(p => p !== playerToRemove);
    const updatedMatches = matches.filter(m => {
      const allPlayersInMatch = [...m.pair1, ...m.pair2];
      return !allPlayersInMatch.includes(playerToRemove);
    });

    const newMatches = updatedMatches.map((m, i) => ({ ...m, id: i + 1 }));

    setPlayers(updatedPlayers);
    setMatches(newMatches);
    setSelectedFilters(selectedFilters.filter(f => f !== playerToRemove));
    setPlayerToRemove(null);
    saveState({ players: updatedPlayers, matches: newMatches, view, matchesPerPlayer, tournamentName, activeTournamentId });
  };

  const handleRenamePlayer = (newPlayerName) => {
    const trimmedNewName = newPlayerName.trim();
    if (!trimmedNewName || trimmedNewName === playerToRename) {
      setPlayerToRename(null);
      return;
    }

    if (players.includes(trimmedNewName)) {
      setError('Este nome de jogador já existe.');
      return;
    }

    const updatedPlayers = players.map(p => (p === playerToRename ? trimmedNewName : p));

    const updatedMatches = matches.map(m => {
      const newPair1 = m.pair1.map(p => (p === playerToRename ? trimmedNewName : p));
      const newPair2 = m.pair2.map(p => (p === playerToRename ? trimmedNewName : p));
      return { ...m, pair1: newPair1, pair2: newPair2 };
    });

    const updatedFilters = selectedFilters.map(f => (f === playerToRename ? trimmedNewName : f));

    setPlayers(updatedPlayers);
    setMatches(updatedMatches);
    setSelectedFilters(updatedFilters);
    setPlayerToRename(null);
    setError('');
    saveState({ players: updatedPlayers, matches: updatedMatches, view, matchesPerPlayer, tournamentName, activeTournamentId });
  };

  const toggleFilter = (name) => {
    setSelectedFilters(prev => 
      prev.includes(name) ? prev.filter(f => f !== name) : [...prev, name]
    );
  };
    
  const updateScore = (matchId, pairIdx, delta) => {
    const newMatches = matches.map(m => {
      if (m.id === matchId) {
        const newScore1 = pairIdx === 1 ? m.score1 + delta : m.score1;
        const newScore2 = pairIdx === 2 ? m.score2 + delta : m.score2;
        let autoWinner = m.winner;
        if (newScore1 > newScore2) autoWinner = 1;
        else if (newScore2 > newScore1) autoWinner = 2;
        else if (newScore1 === newScore2) autoWinner = null;
        return { ...m, score1: newScore1, score2: newScore2, winner: autoWinner, completed: true };
      }
      return m;
    });
    setMatches(newMatches);
    saveState({ players, matches: newMatches, view, matchesPerPlayer, tournamentName, activeTournamentId });
  };

  const setWinner = (matchId, pairIdx) => {
    const newMatches = matches.map(m => {
      if (m.id === matchId) {
        const newWinner = m.winner === pairIdx ? null : pairIdx;
        return { ...m, winner: newWinner, completed: true };
      }
      return m;
    });
    setMatches(newMatches);
    saveState({ players, matches: newMatches, view, matchesPerPlayer, tournamentName, activeTournamentId });
  };

  // Timer Functions
  const startMatch = (id) => {
    const newMatches = matches.map(m => {
      if (m.id === id) {
        const now = Date.now();
        return {
          ...m,
          isRunning: true,
          firstStartTime: m.firstStartTime || now,
          lastStartTime: now,
          endTime: null
        };
      }
      return m;
    });
    setMatches(newMatches);
    saveState({ players, matches: newMatches, view, matchesPerPlayer, tournamentName, activeTournamentId });
  };

  const pauseMatch = (id) => {
    const newMatches = matches.map(m => {
      if (m.id === id && m.isRunning) {
        return {
          ...m,
          isRunning: false,
          accumulatedTime: m.accumulatedTime + (Date.now() - m.lastStartTime)
        };
      }
      return m;
    });
    setMatches(newMatches);
    saveState({ players, matches: newMatches, view, matchesPerPlayer, tournamentName, activeTournamentId });
  };

  const stopMatch = (id) => {
    const newMatches = matches.map(m => {
      if (m.id === id) {
        const now = Date.now();
        const sessionTime = m.isRunning ? now - m.lastStartTime : 0;
        return {
          ...m,
          isRunning: false,
          accumulatedTime: m.accumulatedTime + sessionTime,
          endTime: now,
          completed: true
        };
      }
      return m;
    });
    setMatches(newMatches);
    saveState({ players, matches: newMatches, view, matchesPerPlayer, tournamentName, activeTournamentId });
  };

  const resetMatchTime = (id) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm("This will erase all time records for this game. Confirm?")) return;
    const newMatches = matches.map(m => m.id === id ? { ...m, isRunning: false, firstStartTime: null, lastStartTime: null, endTime: null, accumulatedTime: 0 } : m);
    setMatches(newMatches);
    saveState({ players, matches: newMatches, view, matchesPerPlayer, tournamentName, activeTournamentId });
  };

  const getMatchTime = (m) => {
    let total = m.accumulatedTime;
    if (m.isRunning && m.lastStartTime) {
      total += (currentTime - m.lastStartTime);
    }
    return total;
  };

  const formatDuration = (ms) => {
    if (!ms || ms < 0) return '00:00:00';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatTimeStr = (ts) => {
    if (!ts) return null;
    return new Date(ts).toLocaleTimeString('pt-BR');
  };
    
  const ranking = useMemo(() => {
    const stats = {};
    players.forEach(p => { stats[p] = { name: p, points: 0, wins: 0, games: 0 }; });
    matches.forEach(m => {
      if (!m.completed) return;
      m.pair1.forEach(p => {
        if (!stats[p]) return;
        stats[p].points += m.score1;
        stats[p].games += 1;
        if (m.winner === 1) stats[p].wins += 1;
      });
      m.pair2.forEach(p => {
        if (!stats[p]) return;
        stats[p].points += m.score2;
        stats[p].games += 1;
        if (m.winner === 2) stats[p].wins += 1;
      });
    });
    return Object.values(stats).sort((a, b) => b.wins !== a.wins ? b.wins - a.wins : b.points - a.points);
  }, [matches, players]);

  const activeMatch = matches.find(m => m.id === activeMatchId);
  const nextMatch = matches.find(m => m.id === activeMatchId + 1);

  const archiveTournament = async () => {
    if (!user || (players.length === 0 && matches.length === 0)) return;

    const tournamentToSave = {
      name: tournamentName,
      players,
      matches,
      matchesPerPlayer,
      timestamp: activeTournamentId || new Date().toISOString(),
    };

    const docId = tournamentToSave.timestamp;
    const docRef = doc(db, 'users', user.uid, 'archivedTournaments', docId);
    await setDoc(docRef, tournamentToSave);
  };

  const handleReset = async () => {
    await archiveTournament();
    
    if(user) {
      const docRef = doc(db, 'users', user.uid, 'tournaments', 'current');
      await deleteDoc(docRef);
    }
    
    setMatches([]);
    setPlayers([]);
    setSelectedFilters([]);
    setTournamentName(`Torneio ${new Date().toLocaleString()}`);
    setActiveTournamentId(null);
    setView('setup');
    setShowResetConfirm(false);
  };

  const loadTournament = (tournamentData) => {
    setPlayers(tournamentData.players);
    setMatches(tournamentData.matches);
    setMatchesPerPlayer(tournamentData.matchesPerPlayer);
    setTournamentName(tournamentData.name);
    setActiveTournamentId(tournamentData.timestamp);
    setView('matches');
    setShowPrevious(false);
    saveState({ 
      players: tournamentData.players, 
      matches: tournamentData.matches, 
      matchesPerPlayer: tournamentData.matchesPerPlayer, 
      tournamentName: tournamentData.name, 
      activeTournamentId: tournamentData.timestamp,
      view: 'matches'
    });
  };
  
  if (showPrevious) {
    return <PreviousTournaments onLoadTournament={loadTournament} onBack={() => setShowPrevious(false)} user={user} />;
  }

  if (loading) {
    return <div className="min-h-screen bg-slate-950 text-slate-100 font-sans p-4 flex justify-center items-center">Loading...</div>;
  }
  
  if (!user) {
    return <LandingPage onLogin={login} />;
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-sans p-4 pb-32">
      <div className="max-w-2xl mx-auto">
          <>
            <SaveStatus isSaving={isSaving} />
            <div className="flex justify-between items-center mb-4">
              <p>Welcome, {user.displayName}</p>
              <button onClick={logout} className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded">
                Logout
              </button>
            </div>
            {view !== 'match_detail' && <Header />}
            <Navigation view={view} setView={setView} setShowResetConfirm={setShowResetConfirm} showSetup={matches.length > 0} onShowPrevious={() => setShowPrevious(true)} />

            {showResetConfirm && (
              <ResetModal handleReset={handleReset} setShowResetConfirm={setShowResetConfirm} />
            )}

            <RemovePlayerModal
                playerName={playerToRemove}
                onConfirm={confirmRemovePlayer}
                onCancel={() => setPlayerToRemove(null)}
            />

            <RenamePlayerModal
              currentPlayerName={playerToRename}
              onRename={handleRenamePlayer}
              onCancel={() => setPlayerToRename(null)}
            />
            
            {showConfirmUpdate && (
                <ConfirmUpdateModal
                    uncompletedCount={matches.filter(m => !m.completed).length}
                    onConfirm={confirmUpdateAndProceed}
                    onCancel={() => setShowConfirmUpdate(false)}
                />
            )}

            {error && (
              <div className="bg-rose-500/10 border border-rose-500/50 text-rose-400 p-4 rounded-2xl mb-6 text-sm flex items-center gap-3 animate-pulse">
                <Info className="w-5 h-5 shrink-0" /> {error}
              </div>
            )}

            <main>
              {view === 'setup' && (
                <Setup 
                  players={players}
                  playerName={playerName}
                  setPlayerName={setPlayerName}
                  addPlayer={addPlayer}
                  removePlayer={setPlayerToRemove}
                  renamePlayer={setPlayerToRename}
                  validOptions={validOptions}
                  matchesPerPlayer={matchesPerPlayer}
                  setMatchesPerPlayer={setMatchesPerPlayer}
                  generateTournament={handleGenerateOrUpdate}
                  hasMatches={matches.length > 0}
                />
              )}

              {view === 'matches' && (
                <Matches
                  matches={filteredMatches}
                  players={players}
                  selectedFilters={selectedFilters}
                  toggleFilter={toggleFilter}
                  setSelectedFilters={setSelectedFilters}
                  partnerMapping={partnerMapping}
                  updateScore={updateScore}
                  setWinner={setWinner}
                  getMatchTime={getMatchTime}
                  formatDuration={formatDuration}
                  setActiveMatchId={setActiveMatchId}
                  setView={setView}
                />
              )}

              {view === 'match_detail' && activeMatch && (
                <MatchDetail
                  activeMatch={activeMatch}
                  nextMatch={nextMatch}
                  setView={setView}
                  startMatch={startMatch}
                  pauseMatch={pauseMatch}
                  stopMatch={stopMatch}
                  resetMatchTime={resetMatchTime}
                  getMatchTime={getMatchTime}
                  formatDuration={formatDuration}
                  formatTimeStr={formatTimeStr}
                  setWinner={setWinner}
                  updateScore={updateScore}
                  setActiveMatchId={setActiveMatchId}
                />
              )}

              {view === 'ranking' && (
                <Ranking ranking={ranking} />
              )}
            </main>

            {matches.length > 0 && view !== 'setup' && view !== 'match_detail' && hasCompletedMatches && (
              <FloatingSummary ranking={ranking} />
            )}
          </>
      </div>
    </div>
  );
};

export default App;
